import {
  endOfMonth,
  endOfWeek,
  isAfter,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { EventInstanceStore } from '../../../stores/EventInstanceStore';
import { ScheduleStore } from '../../../stores/ScheduleStore';
import { UserStore } from '../../../stores/UserStore';
import { CalendarView, DaySchedule, Training } from '../types/calendarTypes';
import {
  convertInstanceToTraining,
  convertTemplateToTraining,
} from './trainingUtils';

/**
 * Builds schedules data for calendar view
 */
export const buildSchedulesData = (
  eventInstanceStore: EventInstanceStore,
  scheduleStore: ScheduleStore,
  userStore: UserStore,
  calendarView: CalendarView,
  calendarCurrentDate: Date,
  formatDate: (date: Date, format: string) => string
): DaySchedule[] => {
  // Get all dates that have instances
  const instanceDates = new Set(eventInstanceStore.instancesByDate.keys());

  // Get all dates that should have templates (for the current calendar view)
  const templateDates = new Set<string>();
  const today = startOfDay(new Date());

  // For week view, get dates for the current week
  if (calendarView === 'week') {
    const weekStart = startOfWeek(calendarCurrentDate, { weekStartsOn: 1 }); // Monday
    const weekEnd = endOfWeek(calendarCurrentDate, { weekStartsOn: 1 }); // Sunday

    for (
      let d = new Date(weekStart);
      d <= weekEnd;
      d.setDate(d.getDate() + 1)
    ) {
      // Only show templates for future dates
      if (isAfter(d, today)) {
        const templatesForDate = scheduleStore.getTemplatesForDate(d);
        if (templatesForDate.length > 0) {
          templateDates.add(formatDate(d, 'yyyy-MM-dd'));
        }
      }
    }
  }

  // For month view, get dates for the current month
  if (calendarView === 'month') {
    const monthStart = startOfMonth(calendarCurrentDate);
    const monthEnd = endOfMonth(calendarCurrentDate);

    for (
      let d = new Date(monthStart);
      d <= monthEnd;
      d.setDate(d.getDate() + 1)
    ) {
      // Only show templates for future dates
      if (isAfter(d, today)) {
        const templatesForDate = scheduleStore.getTemplatesForDate(d);
        if (templatesForDate.length > 0) {
          templateDates.add(formatDate(d, 'yyyy-MM-dd'));
        }
      }
    }
  }

  // Combine all dates
  const allDates = new Set([
    ...Array.from(instanceDates),
    ...Array.from(templateDates),
  ]);

  return Array.from(allDates).map((date: string) => {
    const instances = eventInstanceStore.instancesByDate.get(date) || [];
    const dateObj = new Date(date);
    const templates = scheduleStore.getTemplatesForDate(dateObj);

    // Convert instances to Training format
    const instanceTimeSlots = instances.map((instance) =>
      convertInstanceToTraining(instance, date, userStore, formatDate)
    );

    // If there are instances, only show instances (don't show templates)
    if (instances.length > 0) {
      return {
        date,
        timeSlots: instanceTimeSlots,
      };
    }

    // If no instances, check if date is in the future before showing templates
    if (isAfter(dateObj, today)) {
      const templateTimeSlots = templates.map((template) =>
        convertTemplateToTraining(template, dateObj, userStore)
      );

      return {
        date,
        timeSlots: templateTimeSlots,
      };
    }

    // For past dates with no instances, return empty schedule
    return {
      date,
      timeSlots: [],
    };
  });
};

/**
 * Gets schedule for a specific date
 */
export const getScheduleForDate = (
  date: Date,
  eventInstanceStore: EventInstanceStore,
  scheduleStore: ScheduleStore,
  userStore: UserStore,
  formatDate: (date: Date, format: string) => string
): DaySchedule | undefined => {
  // Validate the date
  if (!date || isNaN(date.getTime())) {
    return undefined;
  }

  const dateStr = formatDate(date, 'yyyy-MM-dd');
  const instancesForDate =
    eventInstanceStore.instancesByDate.get(dateStr) || [];
  const today = startOfDay(new Date());

  // If there are instances, only show instances (don't show templates)
  if (instancesForDate.length > 0) {
    const instanceTimeSlots: Training[] = instancesForDate.map((instance) =>
      convertInstanceToTraining(instance, dateStr, userStore, formatDate)
    );

    return {
      date: dateStr,
      timeSlots: instanceTimeSlots,
    };
  }

  // If no instances, check for templates only for future dates
  if (isAfter(date, today)) {
    const templatesForDate = scheduleStore.getTemplatesForDate(date);

    // If no instances and no templates, return undefined
    if (templatesForDate.length === 0) {
      return undefined;
    }

    // Convert templates to Training format
    const templateTimeSlots: Training[] = templatesForDate.map((template) =>
      convertTemplateToTraining(template, date, userStore)
    );

    return {
      date: dateStr,
      timeSlots: templateTimeSlots,
    };
  }

  // For past dates with no instances, return undefined (will show empty state)
  return undefined;
};
